import { FC, useState, createContext, useContext } from 'react';
import noop from 'lodash/noop';
import { ALERT_MESSAGE } from 'config/constants';
import { Amendment } from 'types';
import { useDetailsBooking } from 'lib/hooks';
import { trackUpdatedValue, trackEditDetailsPopUp, FormValues } from './tracking';
import { useSoftAmendment } from '../lib/useSoftAmendment';
import useAlert from 'hooks/useAlert';

interface ContextValue {
  onSubmit: Function;
  loading: Boolean;
  complete: Boolean;
  amendment?: Amendment;
  error?: boolean;
}

const SoftAmendmentContext = createContext<ContextValue>({
  onSubmit: noop,
  loading: false,
  complete: false,
});

export const SoftAmendmentProvider: FC = ({ children }) => {
  const { booking, refetch } = useDetailsBooking();
  const { createAndAcceptSoftAmendment, loading, error } = useSoftAmendment();
  const [amendment, setAmendment] = useState<Amendment>();
  const [complete, setComplete] = useState(false);
  const { displayAlert } = useAlert();

  // TODO: GARD-1003 : Update GA tracking helpers (e.g. trackUpdatedValue/trackEditDetailsPopUp) to support new nullable booking types.
  // For now we coerce optional fields to non-null strings to satisfy tracking helper typings.
  const bookingDetails = {
    email: booking.travelArranger.email,
    phone: booking.travelArranger.phone ?? '',
    qffNumber: booking.reservation.leadGuest.qffNumber || '',
    businessNumber: booking.travelArranger.businessRegistrationNumber,
  };

  const onSubmit = async ({ name, ...values }: FormValues) => {
    const completedAmendment = await createAndAcceptSoftAmendment({ bookingId: booking.id, input: values });
    setAmendment(completedAmendment);

    if (completedAmendment) {
      trackUpdatedValue(bookingDetails, values);
      trackEditDetailsPopUp();
      refetch();
      displayAlert({ type: 'success', message: ALERT_MESSAGE.AMENDMENT_SUCCESS });
      setComplete(true);
    }
  };

  return (
    <SoftAmendmentContext.Provider value={{ onSubmit, amendment, complete, loading, error }}>{children}</SoftAmendmentContext.Provider>
  );
};

export const useSoftAmendmentContext = () => useContext(SoftAmendmentContext);
